import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import StepControls from 'pages/CarePlanBuilder/components/StepControls'
import Header from 'components/Header'
import { PageContainer, Steps } from 'ui/styled'
import { Spin } from 'antd'
import TypeStep from './steps/TypeStep'
import CareStep from './steps/CareStep'
import NoteStep from './steps/NoteStep'
import SummaryStep from './steps/SummaryStep'
import useCarePlanBuilder from './hooks/useCarePlanBuilder'
import ContentWrapper from './styled'

/* eslint-disable react/prop-types */
const getPlanBuilderSteps = ({ noEntities }) => [
  {
    title: 'Care Plan Type',
    hintText: 'Select the care plan type',
    Content: ({ selectEntityType }) => (
      <TypeStep selectEntityType={selectEntityType} />
    )
  },
  {
    title: 'Care',
    hintText: 'Select the best care options for the recipient',
    disabled: noEntities,
    Content: ({
      carePlan,
      setLoadingSpin,
      careStepFilters,
      resetCareStepFilters,
      requestType
    }) => (
      <CareStep
        carePlan={carePlan}
        setLoading={setLoadingSpin}
        careStepFilters={careStepFilters}
        resetCareStepFilters={resetCareStepFilters}
        requestType={requestType}
      />
    )
  },
  {
    title: 'Note',
    hintText: 'Make any annotations that should be visible in the care plan',
    disabled: false,
    Content: ({ carePlan, setLoadingSpin }) => (
      <NoteStep carePlan={carePlan} setLoading={setLoadingSpin} />
    )
  },
  {
    title: 'Summary',
    hintText: 'Great, the care plan is now Complete!',
    disabled: false,
    Content: ({ carePlan, setLoadingSpin }) => (
      <SummaryStep carePlan={carePlan} setLoading={setLoadingSpin} />
    )
  }
]
/* eslint-enable react/prop-types */

const CarePlanBuilder = ({
  modalView = false,
  careplan_id = null,
  setViewMode = () => {},
  carePlanFilters = {},
  requestType = ''
}) => {
  const params = useParams()
  const [loadingSpin, setLoadingSpin] = useState(false)
  const [careStepFilters, setCareStepFilters] = useState(carePlanFilters)

  const resetCareStepFilters = () => setCareStepFilters()

  const id = modalView ? careplan_id : params?.id

  const {
    loading,
    carePlan,
    carePlanTitle,
    step,
    setStep,
    selectEntityType,
    onCarePlanAction,
    noEntities
  } = useCarePlanBuilder(id, modalView, setViewMode)

  const PBSteps = useMemo(
    () => getPlanBuilderSteps({ noEntities }),
    [noEntities]
  )

  const { Content, ...currentStep } = PBSteps[step]

  return (
    <>
      <Header
        title={`New Care Plan ${carePlanTitle}`}
        isArrowHidden
        loading={loading}
        modalView={modalView}
      />
      <PageContainer modalView={modalView}>
        <Steps current={step} items={PBSteps.map(({ title }) => ({ title }))} />
        <Spin spinning={loadingSpin || loading}>
          <StepControls
            step={step}
            setStep={setStep}
            disabled={currentStep.disabled}
            hintText={currentStep.hintText}
            onCarePlanAction={onCarePlanAction}
            modalView={modalView}
            carePlan={carePlan}
          />
          <ContentWrapper>
            <Content
              carePlan={carePlan}
              careStepFilters={careStepFilters}
              resetCareStepFilters={resetCareStepFilters}
              requestType={requestType}
              selectEntityType={selectEntityType}
              setLoadingSpin={setLoadingSpin}
            />
          </ContentWrapper>
        </Spin>
      </PageContainer>
    </>
  )
}

CarePlanBuilder.propTypes = {
  modalView: PropTypes.bool,
  careplan_id: PropTypes.string,
  setViewMode: PropTypes.func,
  carePlanFilters: PropTypes.object,
  requestType: PropTypes.string
}

export default CarePlanBuilder
